/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik, FormikProvider, FieldArray} from 'formik'
import classNames from 'classnames'
import Dropdown from 'react-bootstrap/Dropdown'

import {KTSVG, InvoiceType, TaxType, WitholdingType} from '../../../helpers'
import {
  KTCard,
  KTCardBody,
  InputPrice,
  Select,
  SelectCreatable,
  Loading,
  Datepicker,
} from '../../../components'
import {PageTitle} from '../../../core'
import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedBreadcrumbs,
  useMemoizedFormValues,
} from '../../../utils/performanceOptimizations'

import {useDispatch, useSelector} from 'react-redux'
import {
  projectLookup,
  allProductForUpdate,
  allProductLookup,
  warehouseLookup,
  basicLookup,
} from '../../../redux/slices/lookup.slice'
import {saleinvoiceUpdate, saleinvoiceGetId} from '../../../redux/slices/saleinvoice.slice'
import {productGetInfo, productCreateFast} from '../../../redux/slices/product.slice'

const selectLookupData = (state) => ({
  allProductForUpdateList: state.lookup.allProductForUpdateList,
  projectLookupList: state.lookup.projectLookupList,
  warehouseLookupList: state.lookup.warehouseLookupList,
  ProductUnit: state.lookup.ProductUnit,
  DiscountType: state.lookup.DiscountType,
  InvoiceKind: state.lookup.InvoiceKind,
  loading: state.lookup.loading,
})

const selectSaleInvoiceData = (state) => ({
  isRedirect: state.saleinvoice.isRedirect,
  detail: state.saleinvoice.detail,
  loading: state.saleinvoice.loading,
})

const selectProductData = (state) => ({
  detail: state.product.detail,
  isRedirect: state.product.isRedirect,
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {
    allProductForUpdateList,
    projectLookupList,
    warehouseLookupList,
    ProductUnit,
    DiscountType,
    InvoiceKind,
    loading,
  } = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectSaleInvoiceData)
  const {detail: product, isRedirect: createProduct} = useSelector(selectProductData)
  const dispatch = useDispatch()
  const [changeItem, setChangeItem] = useState()
  const [changeData, setChangeData] = useState()
  const [changeProduct, setChangeProduct] = useState()
  const [createItem, setCreateItem] = useState()
  const [visibiltyItem, setVisibiltyItem] = useState([
    {
      Discount: false,
      Description: false,
      WitholdingRate: false,
    },
  ])

  const formikRef = useRef()
  const isFormPopulatedRef = useRef(false)

  useEffect(() => {
    dispatch(saleinvoiceGetId(id))
    dispatch(projectLookup())
    dispatch(allProductForUpdate())
    dispatch(allProductLookup())
    dispatch(warehouseLookup())
    dispatch(basicLookup('ProductUnit'))
    dispatch(basicLookup('DiscountType'))
    dispatch(basicLookup('InvoiceKind'))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() =>
    Yup.object().shape({
      Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      InvoiceKindId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      SaleInvoiceItems: Yup.array().of(
        Yup.object().shape({
          ProductId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
          Amount: Yup.number()
            .typeError(intl.formatMessage({id: 'REQUIRED.VALID'}))
            .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
          UnitPrice: Yup.number()
            .typeError(intl.formatMessage({id: 'REQUIRED.VALID'}))
            .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
          ProductUnitId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
          TaxId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        })
      ),
    }), [intl]
  )

  const formValues = useMemoizedFormValues({
    Id: id,
    Name: '',
    InvoiceTypeId: 1,
    ProjectId: null,
    EditDate: new Date(),
    DefermentDate: new Date(),
    InvoiceNumber: null,
    SubTotalPrice: 0,
    TotalTax: 0,
    TotalDiscount: 0,
    GrossTotalPrice: 0,
    TotalWitholding: 0,
    TotalPrice: 0,
    InvoiceKindId: '',
    SaleInvoiceItems: [
      {
        Id: 0,
        ProductId: null,
        WarehouseId: null,
        Amount: 1,
        UnitPrice: 0,
        ProductUnitId: 1,
        TaxId: 6,
        TotalTax: null,
        SubTotalPrice: 0,
        TotalPrice: 0,
        Description: null,
        DiscountTypeId: 1,
        DiscountRate: 0,
        Discount: null,
        WitholdingRateId: null,
        Witholding: null,
      },
    ],
  })

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      setSubmitting(true)
      dispatch(saleinvoiceUpdate(values))
      resetForm()
    },
  })

  formikRef.current = formik

  useEffect(() => {
    if (detail && !isFormPopulatedRef.current) {
      for (const property in detail) {
        const propVal = property.charAt(0).toUpperCase() + property.slice(1)

        if (property == 'saleInvoiceItemModels') {
          detail['saleInvoiceItemModels'].map((item, index) => {
            for (const invoice in item) {
              const propValArr = invoice.charAt(0).toUpperCase() + invoice.slice(1)
              formik.setFieldValue(`SaleInvoiceItems[${index}][${propValArr}]`, item[invoice])
            }
          })
        }

        for (const values in formValues) {
          if (values === propVal) {
            formik.setFieldValue(
              propVal,
              property === 'editDate' || property === 'defermentDate'
                ? new Date(detail[property])
                : detail[property]
            )
          }
        }
      }
      isFormPopulatedRef.current = true
    }
  }, [detail, formValues])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.SALESINVOICES'}),
      path: '/sales/sales-invoices',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/sales/sales-invoices/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirect) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirect, navigate, pageUrl, id])

  const onChangeItem = useCallback((target, value, index) => {
    setChangeItem({index, target})
    formik.setFieldValue(`SaleInvoiceItems[${index}][${target}]`, value)
  }, [formik])

  const sumArrayItem = useCallback((item) => {
    return formik.values.SaleInvoiceItems.reduce((a, v) => (a = a + v[item]), 0)
  }, [formik.values.SaleInvoiceItems])

  useEffect(() => {
    const saleInvoiceItems = formik.values.SaleInvoiceItems[changeItem && changeItem.index]

    if (saleInvoiceItems) {
      const subTotalPrice = Number((saleInvoiceItems.UnitPrice * saleInvoiceItems.Amount).toFixed(4))
      const discount = Number((
        saleInvoiceItems.DiscountTypeId == 1
          ? (subTotalPrice * saleInvoiceItems.DiscountRate) / 100
          : saleInvoiceItems.DiscountRate
      ).toFixed(4))
      const tax = TaxType.find((item) => item.id == saleInvoiceItems.TaxId)
      const totalTax = Number((((subTotalPrice - discount) * (tax && tax.value || 0)) / 100).toFixed(4))
      const witholdingRate = WitholdingType.find(
        (item) => item.id == saleInvoiceItems.WitholdingRateId
      )
      const witholding = Number(((totalTax * (witholdingRate && witholdingRate.value || 0)) / 100).toFixed(4))
      const totalPrice = Number((subTotalPrice + totalTax - (witholding + discount)).toFixed(4))

      formik.setFieldValue(`SaleInvoiceItems[${changeItem && changeItem.index}].SubTotalPrice`, subTotalPrice)
      formik.setFieldValue(`SaleInvoiceItems[${changeItem && changeItem.index}].Discount`, discount)
      formik.setFieldValue(`SaleInvoiceItems[${changeItem && changeItem.index}].TotalTax`, totalTax)
      formik.setFieldValue(`SaleInvoiceItems[${changeItem && changeItem.index}].Witholding`, witholding)
      formik.setFieldValue(`SaleInvoiceItems[${changeItem && changeItem.index}].TotalPrice`, totalPrice)

      setChangeData(totalPrice)
    }
  }, [changeItem])

  useEffect(() => {
    formik.setFieldValue('SubTotalPrice', sumArrayItem('SubTotalPrice'))
    formik.setFieldValue('TotalTax', sumArrayItem('TotalTax'))
    formik.setFieldValue('TotalDiscount', sumArrayItem('Discount'))
    formik.setFieldValue(
      'GrossTotalPrice',
      sumArrayItem('SubTotalPrice') - sumArrayItem('Discount')
    )
    formik.setFieldValue('TotalWitholding', sumArrayItem('Witholding'))
    formik.setFieldValue('TotalPrice', sumArrayItem('TotalPrice'))
  }, [changeData])

  const onClickNewItem = useCallback((arrayHelpers) => {
    arrayHelpers.push({
      Id: 0,
      ProductId: null,
      WarehouseId: null,
      Amount: 1,
      UnitPrice: 0,
      ProductUnitId: 1,
      TaxId: 6,
      TotalTax: null,
      SubTotalPrice: 0,
      TotalPrice: 0,
      Description: null,
      DiscountTypeId: 1,
      DiscountRate: 0,
      Discount: null,
      WitholdingRateId: null,
      Witholding: null,
    })
    setVisibiltyItem([
      ...visibiltyItem,
      {
        Discount: false,
        Description: false,
        WitholdingRate: false,
      },
    ])
  }, [visibiltyItem])

  const onClickVisibityItem = useCallback((item, index) => {
    const newArr = [...visibiltyItem]
    newArr[index][item] = !newArr[index][item]
    setVisibiltyItem(newArr)
  }, [visibiltyItem])

  const onChangeProduct = useCallback((item, index) => {
    formik.setFieldValue(`SaleInvoiceItems[${index}].ProductId`, Number(item && item.value))
    setChangeProduct(index)
    dispatch(productGetInfo({Id: item && item.value}))
  }, [formik, dispatch])

  useEffect(() => {
    if (product) {
      formik.setFieldValue(
        `SaleInvoiceItems[${changeProduct}].ProductUnitId`,
        product && product.productUnitId
      )
      formik.setFieldValue(`SaleInvoiceItems[${changeProduct}].UnitPrice`, product && product.baseSalePrice)
      formik.setFieldValue(`SaleInvoiceItems[${changeProduct}].TaxId`, product && product.taxId)
      setChangeItem({index: changeProduct, target: 'UnitPrice'})
    }
  }, [product, changeProduct])

  const onClickFastProduct = useCallback((Name, Index) => {
    dispatch(productCreateFast({Name}))
    setCreateItem({Name, Index})
  }, [dispatch])

  useEffect(() => {
    if (createProduct) dispatch(allProductLookup())
  }, [createProduct, dispatch])

  useEffect(() => {
    if (allProductForUpdateList && createProduct) {
      formik.setFieldValue(`SaleInvoiceItems[${createItem && createItem.Index}].ProductId`, createProduct.id)
    }
  }, [allProductForUpdateList, createProduct, createItem])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.SALESINVOICES.EDIT'})}
      </PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard>
            <KTCardBody>
              <FormikProvider value={formik}>
                {detail && (
                  <form className='form' onSubmit={formik.handleSubmit} noValidate>
                    <div className='row mb-6'>
                      <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'SALEINVOICE.CREATE.NAME'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <input
                          type='text'
                          className='form-control form-control-solid mb-3 mb-lg-0'
                          autoComplete='off'
                          required
                          disabled={formik.isSubmitting}
                          readOnly={detail?.gibInvoiceStatusId == 2}
                          {...formik.getFieldProps('Name')}
                        />
                        {formik.touched.Name && formik.errors.Name && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.Name}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'SALEINVOICE.CREATE.COMPANYID'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <span className='fw-bold fs-6'>{detail.companyName}</span>
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                        {intl.formatMessage({id: 'SALEINVOICE.CREATE.PROJECTID'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <Select
                          data={projectLookupList}
                          required
                          {...formik.getFieldProps('ProjectId')}
                          onChange={(item) =>
                            formik.setFieldValue('ProjectId', Number(item?.value))
                          }
                          disabled={detail?.gibInvoiceStatusId == 2}
                        />
                        {formik.touched.ProjectId && formik.errors.ProjectId && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.ProjectId}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                        {intl.formatMessage({id: 'SALEINVOICE.CREATE.INVOICEKINDNAME'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <Select
                          data={InvoiceKind}
                          required
                          {...formik.getFieldProps('InvoiceKindId')}
                          onChange={(item) =>
                            formik.setFieldValue('InvoiceKindId', Number(item?.value))
                          }
                        />
                        {formik.touched.InvoiceKind && formik.errors.InvoiceKind && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.InvoiceKind}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'SALEINVOICE.CREATE.INVOICETYPEID'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container d-flex gap-10'>
                        {InvoiceType.map((item, index) => {
                          return (
                            <div
                              className='form-check form-check-custom form-check-solid form-check-sm'
                              key={index}
                            >
                              <input
                                className='form-check-input'
                                name='InvoiceTypeId'
                                type='radio'
                                value={formik.values.InvoiceTypeId}
                                id={`InvoiceTypeId-${index}`}
                                onChange={() => formik.setFieldValue('InvoiceTypeId', item.Id)}
                                checked={item.Id === formik.values.InvoiceTypeId}
                                disabled={detail?.gibInvoiceStatusId == 2}
                              />
                              <label
                                className='form-check-label'
                                htmlFor={`InvoiceTypeId-${index}`}
                              >
                                {intl.formatMessage({id: item.Name})}
                              </label>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'SALEINVOICE.CREATE.EDITDATE'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <Datepicker
                          {...formik.getFieldProps('EditDate')}
                          onChange={([date]) => formik.setFieldValue('EditDate', date)}
                          disabled={detail?.gibInvoiceStatusId == 2}
                        />
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'SALEINVOICE.CREATE.DEFERMENTDATE'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <Datepicker
                          {...formik.getFieldProps('DefermentDate')}
                          onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                          disabled={detail?.gibInvoiceStatusId == 2}
                        />
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'SALEINVOICE.CREATE.INVOICENUMBER'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <input
                          type='text'
                          className='form-control form-control-solid mb-3 mb-lg-0'
                          autoComplete='off'
                          disabled={formik.isSubmitting}
                          {...formik.getFieldProps('InvoiceNumber')}
                          readOnly={detail?.gibInvoiceStatusId == 2}
                        />
                      </div>
                    </div>

                    <FieldArray
                      name='SaleInvoiceItems'
                      render={(arrayHelpers) => (
                        <>
                          <div className='table-responsive'>
                            <table className='table g-5 gs-0 mb-0 fw-bold text-gray-700'>
                              <thead>
                                <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.PRODUCTID',
                                    })}
                                  </th>
                                  {formik.values.InvoiceTypeId === 1 && (
                                    <th>
                                      {intl.formatMessage({
                                        id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.WAREHOUSEID',
                                      })}
                                    </th>
                                  )}
                                  <th>
                                    {intl.formatMessage({
                                      id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.AMOUNT',
                                    })}
                                  </th>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.PRODUCTUNITID',
                                    })}
                                  </th>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.UNITPRICE',
                                    })}
                                  </th>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.TAXID',
                                    })}
                                  </th>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.TOTALPRICE',
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>
                              </thead>
                              <tbody>
                                {formik.values.SaleInvoiceItems.map((item, index) => (
                                  <tr className='border-bottom border-bottom-dashed' key={index}>
                                    <td className='pe-7 ps-1'>
                                      <SelectCreatable
                                        data={allProductForUpdateList}
                                        name={`SaleInvoiceItems[${index}].ProductId`}
                                        value={formik.values.SaleInvoiceItems[index].ProductId}
                                        required
                                        onChange={(item) => onChangeProduct(item, index)}
                                        onCreate={(item) => onClickFastProduct(item, index)}
                                        disabled={detail?.gibInvoiceStatusId == 2}
                                      />

                                      {visibiltyItem[index]?.Description && (
                                        <input
                                          type='text'
                                          className='form-control form-control-solid mt-2'
                                          autoComplete='off'
                                          disabled={formik.isSubmitting}
                                          placeholder={intl.formatMessage({
                                            id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.DESCRIPTION',
                                          })}
                                          name={`SaleInvoiceItems[${index}].Description`}
                                          value={formik.values.SaleInvoiceItems[index].Description}
                                          onChange={formik.handleChange}
                                          readOnly={detail?.gibInvoiceStatusId == 2}
                                        />
                                      )}
                                    </td>

                                    {formik.values.InvoiceTypeId === 1 && (
                                      <td className='ps-0'>
                                        <Select
                                          data={warehouseLookupList}
                                          name={`SaleInvoiceItems[${index}].WarehouseId`}
                                          value={formik.values.SaleInvoiceItems[index].WarehouseId}
                                          required
                                          onChange={(item) =>
                                            formik.setFieldValue(
                                              `SaleInvoiceItems[${index}].WarehouseId`,
                                              Number(item?.value)
                                            )
                                          }
                                          disabled={detail?.gibInvoiceStatusId == 2}
                                        />
                                      </td>
                                    )}

                                    <td className='ps-0'>
                                      <input
                                        type='number'
                                        className='form-control form-control-solid'
                                        autoComplete='off'
                                        disabled={formik.isSubmitting}
                                        placeholder={intl.formatMessage({
                                          id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.AMOUNT',
                                        })}
                                        name={`SaleInvoiceItems[${index}].Amount`}
                                        value={formik.values.SaleInvoiceItems[index].Amount}
                                        required
                                        onChange={(event) =>
                                          onChangeItem('Amount', Number(event.target.value), index)
                                        }
                                        readOnly={detail?.gibInvoiceStatusId == 2}
                                        onFocus={(event) => event.target.select()}
                                      />

                                      {visibiltyItem[index]?.Discount && (
                                        <div className='input-group mt-2'>
                                          <input
                                            type='number'
                                            className='form-control form-control-solid'
                                            autoComplete='off'
                                            disabled={formik.isSubmitting}
                                            placeholder={intl.formatMessage({
                                              id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.DISCOUNTRATE',
                                            })}
                                            name={`SaleInvoiceItems[${index}].DiscountRate`}
                                            value={
                                              formik.values.SaleInvoiceItems[index].DiscountRate
                                            }
                                            onChange={(event) =>
                                              onChangeItem(
                                                'DiscountRate',
                                                Number(event.target.value),
                                                index
                                              )
                                            }
                                            readOnly={detail?.gibInvoiceStatusId == 2}
                                            onFocus={(event) => event.target.select()}
                                          />
                                          <Select
                                            data={DiscountType}
                                            name={`SaleInvoiceItems[${index}].DiscountTypeId`}
                                            value={
                                              formik.values.SaleInvoiceItems[index].DiscountTypeId
                                            }
                                            required
                                            onChange={(item) =>
                                              onChangeItem(
                                                'DiscountTypeId',
                                                Number(item?.value),
                                                index
                                              )
                                            }
                                            disabled={detail?.gibInvoiceStatusId == 2}
                                          />
                                        </div>
                                      )}
                                    </td>

                                    <td className='ps-0'>
                                      <Select
                                        data={ProductUnit}
                                        name={`SaleInvoiceItems[${index}].ProductUnitId`}
                                        value={formik.values.SaleInvoiceItems[index].ProductUnitId}
                                        required
                                        onChange={(item) =>
                                          onChangeItem('ProductUnitId', Number(item?.value), index)
                                        }
                                        disabled={detail?.gibInvoiceStatusId == 2}
                                      />

                                      {visibiltyItem[index]?.WitholdingRate && (
                                        <Select
                                          data={WitholdingType}
                                          className={'mt-2'}
                                          name={`SaleInvoiceItems[${index}].WitholdingRateId`}
                                          value={
                                            formik.values.SaleInvoiceItems[index].WitholdingRateId
                                          }
                                          required
                                          onChange={(item) =>
                                            onChangeItem(
                                              'WitholdingRateId',
                                              Number(item?.value),
                                              index
                                            )
                                          }
                                          disabled={detail?.gibInvoiceStatusId == 2}
                                        />
                                      )}
                                    </td>

                                    <td className='ps-0'>
                                      <InputPrice
                                        placeholder={intl.formatMessage({
                                          id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.UNITPRICE',
                                        })}
                                        name={`SaleInvoiceItems[${index}].UnitPrice`}
                                        required
                                        value={formik.values.SaleInvoiceItems[index].UnitPrice}
                                        onChange={(value) =>
                                          onChangeItem('UnitPrice', Number(value), index)
                                        }
                                        readOnly={detail?.gibInvoiceStatusId == 2}
                                      />
                                    </td>

                                    <td className='ps-0'>
                                      <Select
                                        data={TaxType}
                                        name={`SaleInvoiceItems[${index}].TaxId`}
                                        value={formik.values.SaleInvoiceItems[index].TaxId}
                                        required
                                        onChange={(item) =>
                                          onChangeItem('TaxId', Number(item?.value), index)
                                        }
                                        disabled={detail?.gibInvoiceStatusId == 2}
                                      />
                                    </td>

                                    <td className='ps-0'>
                                      <InputPrice
                                        placeholder={intl.formatMessage({
                                          id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.TOTALPRICE',
                                        })}
                                        name={`SaleInvoiceItems[${index}].TotalPrice`}
                                        value={formik.values.SaleInvoiceItems[index].TotalPrice}
                                      />
                                    </td>

                                    <td className='pt-5 text-end'>
                                      <div className='d-flex'>
                                        <Dropdown autoClose='outside'>
                                          <Dropdown.Toggle
                                            variant='link'
                                            className='btn btn-sm btn-icon btn-active-color-info'
                                          >
                                            <KTSVG
                                              path='/images/icons/duotune/arrows/arr009.svg'
                                              className='svg-icon-2'
                                            />
                                          </Dropdown.Toggle>

                                          <Dropdown.Menu>
                                            <Dropdown.Item
                                              onClick={() => onClickVisibityItem('Discount', index)}
                                              className={classNames({
                                                active: visibiltyItem[index]?.Discount,
                                              })}
                                            >
                                              {intl.formatMessage({
                                                id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.DISCOUNT',
                                              })}
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                              onClick={() =>
                                                onClickVisibityItem('Description', index)
                                              }
                                              className={classNames({
                                                active: visibiltyItem[index]?.Description,
                                              })}
                                            >
                                              {intl.formatMessage({
                                                id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.DESCRIPTION',
                                              })}
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                              onClick={() =>
                                                onClickVisibityItem('WitholdingRate', index)
                                              }
                                              className={classNames({
                                                active: visibiltyItem[index]?.WitholdingRate,
                                              })}
                                            >
                                              {intl.formatMessage({
                                                id: 'SALEINVOICE.CREATE.SALEINVOICEITEMS.WITHOLDINGRATEID',
                                              })}
                                            </Dropdown.Item>
                                          </Dropdown.Menu>
                                        </Dropdown>
                                        {detail?.gibInvoiceStatusId != 2 && (
                                          <button
                                            type='button'
                                            className='btn btn-sm btn-icon btn-active-color-primary'
                                            onClick={() => arrayHelpers.remove(index)}
                                          >
                                            <KTSVG
                                              path='/images/icons/duotune/general/gen027.svg'
                                              className='svg-icon-2'
                                            />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className='border-top border-top-dashed align-middle fs-6 fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}>
                                    {detail?.gibInvoiceStatusId != 2 && (
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                                        onClick={() => onClickNewItem(arrayHelpers)}
                                      >
                                        <KTSVG
                                          path='/images/icons/duotune/arrows/arr009.svg'
                                          className='svg-icon-2'
                                        />
                                        {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                                      </button>
                                    )}
                                  </th>
                                  <th
                                    className='border-bottom border-bottom-dashed ps-0 fs-5'
                                    colSpan={2}
                                  >
                                    {intl.formatMessage({id: 'SALEINVOICE.CREATE.SUBTOTALPRICE'})}
                                  </th>
                                  <th className='border-bottom border-bottom-dashed text-end'>
                                    {intl.formatNumber(formik.values.SubTotalPrice, {
                                      style: 'currency',
                                      currency: 'try',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 4,
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>

                                {formik.values.TotalDiscount > 0 && (
                                  <tr className='align-middle fw-bold text-gray-700'>
                                    <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                    <th
                                      className='border-bottom border-bottom-dashed ps-0 fs-5'
                                      colSpan={2}
                                    >
                                      {intl.formatMessage({id: 'SALEINVOICE.CREATE.TOTALDISCOUNT'})}
                                    </th>
                                    <th className='border-bottom border-bottom-dashed text-end'>
                                      {intl.formatNumber(formik.values.TotalDiscount, {
                                        style: 'currency',
                                        currency: 'try',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 4,
                                      })}
                                    </th>
                                    <th>&nbsp;</th>
                                  </tr>
                                )}

                                <tr className='align-middle fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                  <th
                                    className='border-bottom border-bottom-dashed ps-0 fs-5'
                                    colSpan={2}
                                  >
                                    {intl.formatMessage({id: 'SALEINVOICE.CREATE.GROSSTOTALPRICE'})}
                                  </th>
                                  <th className='border-bottom border-bottom-dashed text-end'>
                                    {intl.formatNumber(formik.values.GrossTotalPrice, {
                                      style: 'currency',
                                      currency: 'try',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 4,
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>

                                <tr className='align-middle fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                  <th
                                    className='border-bottom border-bottom-dashed ps-0 fs-5'
                                    colSpan={2}
                                  >
                                    {intl.formatMessage({id: 'SALEINVOICE.CREATE.TOTALTAX'})}
                                  </th>
                                  <th className='border-bottom border-bottom-dashed text-end'>
                                    {intl.formatNumber(formik.values.TotalTax, {
                                      style: 'currency',
                                      currency: 'try',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 4,
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>

                                {formik.values.TotalWitholding > 0 && (
                                  <tr className='align-middle fw-bold text-gray-700'>
                                    <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                    <th
                                      className='border-bottom border-bottom-dashed ps-0 fs-5'
                                      colSpan={2}
                                    >
                                      {intl.formatMessage({
                                        id: 'SALEINVOICE.CREATE.TOTALWITHOLDING',
                                      })}
                                    </th>
                                    <th className='border-bottom border-bottom-dashed text-end'>
                                      {intl.formatNumber(formik.values.TotalWitholding, {
                                        style: 'currency',
                                        currency: 'try',
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 4,
                                      })}
                                    </th>
                                    <th>&nbsp;</th>
                                  </tr>
                                )}

                                <tr className='align-middle fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                  <th className='fs-4 ps-0' colSpan={2}>
                                    {intl.formatMessage({id: 'SALEINVOICE.CREATE.TOTALPRICE'})}
                                  </th>
                                  <th className='text-end fs-4 text-nowrap'>
                                    {intl.formatNumber(formik.values.TotalPrice, {
                                      style: 'currency',
                                      currency: 'try',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 4,
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </>
                      )}
                    />

                    <div className='row pt-15'>
                      <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                        <button
                          className='btn btn-light'
                          onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                        >
                          {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                        </button>
                        {detail?.gibInvoiceStatusId != 2 && (
                          <button
                            type='submit'
                            className='btn btn-primary'
                            data-kt-users-modal-action='submit'
                            disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                          >
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                            </span>
                            {formik.isSubmitting && (
                              <span className='indicator-progress'>
                                {intl.formatMessage({id: 'PLEASE'})}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                )}
              </FormikProvider>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
